import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import type { CartItem } from '../../types';
import { API_URL_SHOPPING } from '../../constants';

type AddCartItemInput = Omit<CartItem, 'cartKey' | 'quantity'> & {
  quantity?: number;
};

type CartContextValue = {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  isCartPreviewOpen: boolean;
  addItem: (item: AddCartItemInput) => Promise<void>;
  updateQuantity: (cartKey: string, quantity: number) => Promise<void>;
  removeItem: (cartKey: string) => Promise<void>;
  clearCart: () => void;
  checkout: () => Promise<void>;
  openCartPreview: () => void;
  closeCartPreview: () => void;
};

const CART_STORAGE_KEY = 'emdk_cart';
const CART_TOKEN_STORAGE_KEY = 'emdk_cart_token';
const CartContext = createContext<CartContextValue | undefined>(undefined);

const readStoredCart = (): CartItem[] => {
  try {
    const storedCart = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!storedCart) return [];

    const parsedCart = JSON.parse(storedCart);
    return Array.isArray(parsedCart) ? parsedCart : [];
  } catch {
    return [];
  }
};

const getCartKey = (productId: number, combinationId?: number | null) =>
  `${productId}:${combinationId ?? 'base'}`;

type ApiCartItem = {
  id: number;
  productId: number;
  combinationId: number;
  productName: string;
  imageUrl?: string | null;
  options?: string[];
  varCode?: string;
  storeId?: number | null;
  quantity: number;
  unitPrice: number;
};

type ApiCart = {
  cartToken: string;
  items: ApiCartItem[];
};

const mapApiCart = (apiCart: ApiCart): CartItem[] =>
  (apiCart.items || []).map((item) => ({
    cartKey: getCartKey(item.productId, item.combinationId),
    backendItemId: item.id,
    productId: item.productId,
    combinationId: item.combinationId,
    name: item.productName,
    price: Number(item.unitPrice || 0),
    image: item.imageUrl || '',
    quantity: item.quantity,
    storeId: item.storeId,
    options: item.options,
    code: item.varCode,
  }));

const clampQuantity = (quantity: number, available?: number | null) => {
  const normalizedQuantity = Math.max(1, Math.floor(Number(quantity) || 1));
  if (available === null || available === undefined) return normalizedQuantity;

  const normalizedAvailable = Math.max(0, Math.floor(Number(available) || 0));
  return Math.min(normalizedQuantity, Math.max(1, normalizedAvailable));
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() =>
    typeof window === 'undefined' ? [] : readStoredCart()
  );
  const [cartToken, setCartToken] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : window.localStorage.getItem(CART_TOKEN_STORAGE_KEY)
  );
  const [isCartPreviewOpen, setIsCartPreviewOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (!cartToken) return;

    axios.get<ApiCart>(`${API_URL_SHOPPING}/${cartToken}`)
      .then((response) => setItems(mapApiCart(response.data)))
      .catch(() => undefined);
  }, [cartToken]);

  const applyApiCart = (apiCart: ApiCart) => {
    if (apiCart.cartToken) {
      setCartToken(apiCart.cartToken);
      window.localStorage.setItem(CART_TOKEN_STORAGE_KEY, apiCart.cartToken);
    }
    setItems(mapApiCart(apiCart));
  };

  const addItem = useCallback(async (item: AddCartItemInput) => {
    const cartKey = getCartKey(item.productId, item.combinationId);
    const quantityToAdd = clampQuantity(item.quantity || 1, item.available);

    setItems((currentItems) => {
      const existingItem = currentItems.find((currentItem) => currentItem.cartKey === cartKey);
      if (!existingItem) {
        return [
          ...currentItems,
          {
            ...item,
            cartKey,
            quantity: quantityToAdd,
          },
        ];
      }

      return currentItems.map((currentItem) =>
        currentItem.cartKey === cartKey
          ? {
              ...currentItem,
              ...item,
              quantity: clampQuantity(currentItem.quantity + quantityToAdd, item.available),
            }
          : currentItem
      );
    });
    setIsCartPreviewOpen(true);

    if (!item.combinationId) return;

    try {
      const response = await axios.post<ApiCart>(`${API_URL_SHOPPING}/items`, {
        cartToken,
        productId: item.productId,
        combinationId: item.combinationId,
        quantity: quantityToAdd,
        imageUrl: item.image,
        options: item.options,
      });
      applyApiCart(response.data);
    } catch {
      // Keep the local cart so the customer does not lose their selection.
    }
  }, [cartToken]);

  const updateQuantity = useCallback(async (cartKey: string, quantity: number) => {
    const targetItem = items.find((item) => item.cartKey === cartKey);
    setItems((currentItems) =>
      currentItems.map((item) =>
        item.cartKey === cartKey
          ? { ...item, quantity: clampQuantity(quantity, item.available) }
          : item
      )
    );

    if (!cartToken || !targetItem?.backendItemId) return;

    try {
      const response = await axios.put<ApiCart>(
        `${API_URL_SHOPPING}/${cartToken}/items/${targetItem.backendItemId}`,
        { quantity: clampQuantity(quantity, targetItem.available) }
      );
      applyApiCart(response.data);
    } catch {
      // Local update remains as a fallback.
    }
  }, [cartToken, items]);

  const removeItem = useCallback(async (cartKey: string) => {
    const targetItem = items.find((item) => item.cartKey === cartKey);
    setItems((currentItems) => currentItems.filter((item) => item.cartKey !== cartKey));

    if (!cartToken || !targetItem?.backendItemId) return;

    try {
      const response = await axios.delete<ApiCart>(`${API_URL_SHOPPING}/${cartToken}/items/${targetItem.backendItemId}`);
      applyApiCart(response.data);
    } catch {
      // Local removal remains as a fallback.
    }
  }, [cartToken, items]);

  const clearCart = useCallback(() => {
    setItems([]);

    if (!cartToken) return;
    axios.delete<ApiCart>(`${API_URL_SHOPPING}/${cartToken}/items`)
      .then((response) => applyApiCart(response.data))
      .catch(() => undefined);
  }, [cartToken]);

  const checkout = useCallback(async () => {
    if (!cartToken) return;

    const response = await axios.post<ApiCart>(`${API_URL_SHOPPING}/${cartToken}/checkout`);
    applyApiCart(response.data);
    setItems([]);
    setCartToken(null);
    window.localStorage.removeItem(CART_TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(CART_STORAGE_KEY);
  }, [cartToken]);

  const value = useMemo(
    () => ({
      items,
      itemCount: items.reduce((total, item) => total + item.quantity, 0),
      subtotal: items.reduce((total, item) => total + item.price * item.quantity, 0),
      isCartPreviewOpen,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      checkout,
      openCartPreview: () => setIsCartPreviewOpen(true),
      closeCartPreview: () => setIsCartPreviewOpen(false),
    }),
    [addItem, checkout, clearCart, isCartPreviewOpen, items, removeItem, updateQuantity]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe usarse dentro de CartProvider');
  }

  return context;
};
